/**
 * <ProtectedRoute> — route gate. Wraps any route subtree that needs an
 * authenticated user. Optionally takes an allowlist of roles.
 *
 *   <Route element={<ProtectedRoute roles={["admin","super_admin"]} />}>
 *     <Route path="users" element={<Users />} />
 *   </Route>
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { Role } from "@/types/api";
import { roleHomePath } from "@/routes/role-home";
import { FirstLoginPasswordGate } from "./FirstLoginPasswordGate";

interface Props {
  roles?: Role[];
  redirectTo?: string;
}

export function ProtectedRoute({ roles, redirectTo = "/login" }: Props) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-svh place-items-center bg-bg">
        <div className="flex flex-col items-center gap-3 text-fg-muted">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (roles && roles.length && !roles.includes(user.role)) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  // A password someone else set must be replaced before anything else.
  return <FirstLoginPasswordGate><Outlet /></FirstLoginPasswordGate>;
}
